import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSettingsPaymentsPublicQueryKey,
  getGetSettingsPaymentsQueryKey,
  useGetSettingsPaymentsPublic,
  usePutSettingsPayments,
} from '@agos/api-client/payments'
import { T, tt, useAuth } from '@agos/shared'
import { ReadFailedBlock, Spinner, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Wallet } from 'lucide-react'

import {
  customerPaymentsOf,
  customerPaymentsPayload,
  customerPaymentsReadOnlyNote,
  mayEditCustomerPayments,
  qrOfferedButUnusable,
  type CustomerPaymentSwitch,
  type CustomerPayments,
} from './customer-payments'

/**
 * *Nastavenia rozvozu → Platby zákazníkov* - FRONTEND.md §9.766, owner O127, on BACKEND.md §10A.418.
 *
 * Four switches: the two *Pravidlá pokladne* that decide card online, moved here from *Platby online - archív* with
 * their wording and their `data-pref` markers, and the two that decide whether a customer is offered *Karta -
 * terminál* and *QR platba*. **A body, not a page** - the hub draws the one `PageHeader` (§9.302).
 *
 * **Read from the public answer for everybody** (`customer-payments.ts` says why), and written through `PUT
 * /settings/payments` with the four together - the right is *Platby online*, and whoever lacks it reads the switches
 * disabled with the note naming it. A failed read draws no switch at all: a switch drawn *off* from an answer that never
 * came is the false claim §9.750 removed, and for a holder of the right one press would save that guess over the other
 * three.
 */
export function CustomerPaymentsSection() {
  const queryClient = useQueryClient()
  const { user, hasPermission } = useAuth()
  const mayEdit = mayEditCustomerPayments(user?.systemRole, hasPermission)
  const { data, isLoading, isError, refetch } = useGetSettingsPaymentsPublic()
  /* A failed read never reaches this: the branch below returns first, including for a refetch that failed over an
     answer still held (react-query keeps `data` and reports the error), so the parse needs no `isError` of its own. */
  const payments = customerPaymentsOf(data)

  const save = usePutSettingsPayments({
    mutation: {
      onSuccess: () => {
        // Both reads: this screen's, and *Platby online*'s staff answer, which a main administrator reads.
        void queryClient.invalidateQueries({ queryKey: getGetSettingsPaymentsPublicQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getGetSettingsPaymentsQueryKey() })
        toast({ title: tt('Nastavenie platieb uložené') })
      },
      // §9.355 - the server's own sentence, with this one for what the envelope cannot carry.
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(error, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const flip = (current: CustomerPayments, which: CustomerPaymentSwitch) => {
    /* A named variable rather than a literal (§9.509): the generated `PaymentSettingsRequest` predates §10A.418's two
       fields, and the excess-property check fires only on a fresh object literal. The server reads all four. */
    const body: Record<string, boolean> = customerPaymentsPayload(current, which)
    save.mutate({ data: body })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20" data-customer-payments-loading>
        <Spinner />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="space-y-5" data-customer-payments>
        <ReadFailedBlock
          marker="read"
          of="customer-payments"
          title={tt('Platby zákazníkov sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>
            Preto tu nie sú ani prepínače - vypnutý prepínač by tvrdil niečo, čo nevieme, a jeho stlačenie by uložilo
            odhad.
          </T>
        </ReadFailedBlock>
      </div>
    )
  }

  const disabled = !mayEdit || save.isPending || payments === null

  return (
    <div className="space-y-5" data-customer-payments>
      <section className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Wallet className="w-4 h-4 text-muted-foreground" />
            <T>Platby zákazníkov</T>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            <T>Ako môže zákazník zaplatiť objednávku z webu.</T>
          </p>
        </div>
        <div className="divide-y divide-border">
          <SwitchRow
            label={tt('Karta ako predvolená platba')}
            desc={tt('Pri objednávke sa najprv ponúkne platba kartou.')}
            value={payments === null ? null : payments.defaultOnline}
            disabled={disabled}
            testId="defaultOnline"
            onToggle={() => payments !== null && flip(payments, 'defaultOnline')}
          />
          <SwitchRow
            label={tt('Karta aj pri vyzdvihnutí')}
            desc={tt('Zákazník môže zaplatiť kartou aj keď si objednávku vyzdvihne sám.')}
            value={payments === null ? null : payments.allowOnlinePickup}
            disabled={disabled}
            testId="allowOnlinePickup"
            onToggle={() => payments !== null && flip(payments, 'allowOnlinePickup')}
          />
          <SwitchRow
            label={tt('Ponúkať Kartu - terminál')}
            desc={tt('Zákazník môže zvoliť platbu kartou pri prevzatí - kuriérovi pri dverách alebo pri vyzdvihnutí.')}
            value={payments === null ? null : payments.offerCardTerminal}
            disabled={disabled}
            testId="offerCardTerminal"
            onToggle={() => payments !== null && flip(payments, 'offerCardTerminal')}
          />
          <SwitchRow
            label={tt('Ponúkať QR platbu')}
            desc={tt(
              'Zákazník zaplatí pri doručení QR kódom, ktorý mu ukáže kuriér. Pri osobnom odbere sa neponúka - pokladňa QR kód zatiaľ ukázať nevie.',
            )}
            value={payments === null ? null : payments.offerQrPay}
            disabled={disabled}
            testId="offerQrPay"
            onToggle={() => payments !== null && flip(payments, 'offerQrPay')}
          />
        </div>
        {payments !== null && qrOfferedButUnusable(payments) && (
          <p
            className="flex items-start gap-1.5 px-5 py-3 text-xs text-amber-500 border-t border-border"
            data-qr-offer-unusable
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            <T>
              QR platba je pre zákazníkov zapnutá, ale kuriér ju teraz nevie ukázať - je vypnutá alebo nie je nastavená v
              záložke QR platba na tejto obrazovke. Zákazníkom sa preto neponúka.
            </T>
          </p>
        )}
        {payments === null && (
          <p className="px-5 py-3 text-xs text-muted-foreground border-t border-border" data-customer-payments-unknown>
            <T>Server neodpovedal všetky nastavenia platieb - neznáme sú označené pomlčkou.</T>
          </p>
        )}
        {!mayEdit && (
          <p className="px-5 py-3 text-[11px] text-amber-500 border-t border-border" data-read-only>
            {customerPaymentsReadOnlyNote()}
          </p>
        )}
      </section>
    </div>
  )
}

/** One switch - *Platby online*'s `PrefRow`, moved with the two rules it drew (§9.750's dash for an unknown value). */
function SwitchRow({
  label,
  desc,
  value,
  disabled,
  testId,
  onToggle,
}: {
  label: string
  desc: string
  /** Null while the value is not known: drawn as a dash, never as a switch that is off. */
  value: boolean | null
  disabled: boolean
  testId: CustomerPaymentSwitch
  onToggle: () => void
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-pref={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      {value === null ? (
        <span className="w-11 shrink-0 text-center text-sm text-muted-foreground" data-pref-state="unknown">
          -
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-label={label}
          disabled={disabled}
          data-pref-state={value ? 'on' : 'off'}
          onClick={onToggle}
          className={cn(
            'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
            value ? 'bg-primary' : 'bg-muted',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
              value ? 'left-[22px]' : 'left-0.5',
            )}
          />
        </button>
      )}
    </div>
  )
}
